import React, { ReactElement, useRef, useEffect, ReactNode } from 'react';
import { TImageResourceInfo } from '../types/types';
import { ArrowLeft, ArrowRight } from '../design-system/icons/icons';

interface GalerylProps {
  type: 'image' | 'text';
  onLoaded?: string;
  currentIndex?: number;
  onIndex?: (data: number) => void;
  resourceForCarousel?: TImageResourceInfo[];
  resourceForTextCarousel?: { content: ReactNode; key: string }[];
}

const ImageGallery: React.FC<GalerylProps> = (
  props: GalerylProps
): ReactElement => {
  const currentIndex = useRef(props.currentIndex || 0);
  const moveDirection = useRef<'left' | 'right' | 'none'>('none');
  const {
    onLoaded: loaded,
    resourceForCarousel,
    resourceForTextCarousel,
    type,
  } = props;

  function scrollToNextText(galleryElement: HTMLDivElement | null) {
    const { scrollWidth, clientWidth } = galleryElement!;
    const maxScrollLeft = scrollWidth - clientWidth;
    let nextScrollLeft =
      galleryElement!.scrollLeft + galleryElement!.offsetWidth;

    if (nextScrollLeft > maxScrollLeft) {
      galleryElement!.scrollTo({
        left: 0,
        behavior: 'auto',
      });
      nextScrollLeft = galleryElement!.scrollLeft + galleryElement!.offsetWidth;
    }

    galleryElement!.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth',
    });
  }

  function scrollToNext(galleryElement: HTMLDivElement | null) {
    let nextIndex;
    if (currentIndex.current < resourceForCarousel!.length - 1) {
      nextIndex = currentIndex.current + 1;
      currentIndex.current = nextIndex;
      props.onIndex!(nextIndex);
      moveDirection.current = 'right';
      galleryElement!.scrollBy({
        left: galleryElement!.offsetWidth,
        behavior: 'smooth',
      });
    } else if (currentIndex.current === resourceForCarousel!.length - 1) {
      currentIndex.current = 0;
      props.onIndex!(0);
      moveDirection.current = 'right';
      galleryElement?.scrollTo({
        left: 0,
        behavior: 'auto',
      });
    }
  }

  function scrollToPrevious(galleryElement: HTMLDivElement | null) {
    if (currentIndex.current > 0) {
      const prevIndex = currentIndex.current - 1;
      currentIndex.current = prevIndex;
      props.onIndex!(prevIndex);
      moveDirection.current = 'left';
      galleryElement!.scrollBy({
        left: -galleryElement!.offsetWidth,
        behavior: 'smooth',
      });
    } else if (currentIndex.current === 0) {
      const prevIndex = resourceForCarousel!.length - 1;
      currentIndex.current = prevIndex;
      moveDirection.current = 'left';
      props.onIndex!(prevIndex);
      galleryElement?.scrollBy({
        left: prevIndex * galleryElement.offsetWidth,
        behavior: 'auto',
      });
    }
  }

  if (type === 'image') {
    useEffect(() => {
      const newIndex = resourceForCarousel!.findIndex(
        element => element.fileName === loaded
      );
      currentIndex.current = newIndex;
      props.onIndex!(newIndex);

      const galleryElement = galleryRef.current;
      galleryElement!.scrollTo({
        left: newIndex * galleryElement!.offsetWidth,
        behavior: 'auto',
      });
      galleryElement?.addEventListener('scroll', handleGalleryScroll);
      return () => {
        galleryElement?.removeEventListener('scroll', handleGalleryScroll);
      };
    }, [resourceForCarousel]);
  } else if (type === 'text') {
    useEffect(() => {
      const intervalId = setInterval(() => {
        scrollToNextText(galleryRef.current);
      }, 3500);
      return () => {
        clearInterval(intervalId);
      };
    }, [type]);
  }

  const galleryRef = useRef<HTMLDivElement>(null);

  const GaleryElement = (elementName: string) => {
    return (
      <img
        src={`/static/resources/${elementName}`}
        className="max-h-[43rem]"
        alt="loaded from Galery"
        key={elementName}
      />
    );
  };

  const handleGalleryScroll = () => {
    const galleryElement = galleryRef.current;

    let round;
    if (moveDirection.current === 'none') {
      round = Math.round;
    } else {
      round = moveDirection.current === 'right' ? Math.ceil : Math.floor;
    }

    const newIndex = round(
      galleryElement!.scrollLeft / galleryElement!.offsetWidth
    );
    if (currentIndex.current !== newIndex) {
      currentIndex.current = newIndex;
      props.onIndex!(newIndex);
    }
  };

  return (
    <div className="flex justify-center w-full h-full max-h-[46rem]">
      {type === 'image' && (
        <>
          <div className="relative w-40 bg-black basis-1/12 max-h-[46rem] md:text-5xl text-2xl flex align-middle items-center justify-center ">
            <button
              type="button"
              onClick={() => scrollToPrevious(galleryRef.current)}
              className=" hover:text-primary"
            >
              <ArrowLeft />
            </button>
          </div>
          <div
            ref={galleryRef}
            className="w-full relative max-h-[46rem] basis-10/12 bg-black flex overscroll-auto md:overscroll-contain overflow-x-scroll scrollbar snap-mandatory snap-x"
            aria-hidden="true"
          >
            {resourceForCarousel!.map(resource => (
              <div
                className="snap-center	shrink-0 w-full h-auto flex  justify-center items-center"
                key={resource.fileName}
              >
                {GaleryElement(resource.fileName)}
              </div>
            ))}
          </div>
          <div className="relative w-40 bg-black basis-1/12 max-h-[46rem] md:text-5xl text-2xl flex align-middle items-center justify-center ">
            <button
              type="button"
              onClick={() => scrollToNext(galleryRef.current)}
              className=" hover:text-primary"
            >
              <ArrowRight />
            </button>
          </div>
        </>
      )}

      {type === 'text' && (
        <div
          ref={galleryRef}
          className="w-full relative max-h-[46rem] bg-black flex overscroll-auto md:overscroll-contain overflow-x-scroll scrollbara snap-mandatory snap-x"
          aria-hidden="true"
        >
          {resourceForTextCarousel!.map((resource, index) => {
            if (index === 0) {
              return (
                <React.Fragment key={resource.key}>
                  <div
                    style={{ left: '100%' }}
                    className="snap-center shrink-0 w-full h-auto flex justify-center items-center"
                  >
                    {
                      resourceForTextCarousel?.[
                        // eslint-disable-next-line no-unsafe-optional-chaining
                        resourceForTextCarousel?.length - 1
                      ].content
                    }
                  </div>
                  <div
                    style={{ left: 0 }}
                    className="snap-center shrink-0 w-full h-auto flex justify-center items-center"
                    key={resource.key}
                  >
                    {resource.content}
                  </div>
                </React.Fragment>
              );
            }
            return (
              <div
                style={{ left: `${(index + 1) * 100}%` }}
                className="snap-center shrink-0 w-full h-auto flex justify-center items-center"
                key={resource.key}
              >
                {resource.content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
